import type {
    ParameterFailureBatch,
    ParameterFailureInsights,
    ParameterFailureMetric,
    ParameterFailureOccurrence,
    ParameterFailureQueryLimits,
    ParameterFailureTruncationReason,
} from './Types';

export interface ParameterFailureAggregation {
    traceName: string;
    occurrences: Map<string, ParameterFailureOccurrence>;
    counts: Map<string, ParameterFailureMetric>;
    scannedObservations: number;
    availableObservations: number;
    limits: ParameterFailureQueryLimits | null;
    refreshedAt: string;
}

export function createParameterFailureAggregation(): ParameterFailureAggregation {
    return {
        traceName: 'AI 应用搭建',
        occurrences: new Map(),
        counts: new Map(),
        scannedObservations: 0,
        availableObservations: 0,
        limits: null,
        refreshedAt: new Date(0).toISOString(),
    };
}

export function mergeParameterFailureBatch(
    aggregation: ParameterFailureAggregation,
    batch: ParameterFailureBatch,
): void {
    aggregation.traceName = batch.traceName;
    for (const occurrence of batch.occurrences) {
        if (aggregation.occurrences.has(occurrence.callKey)) continue;
        aggregation.occurrences.set(occurrence.callKey, occurrence);
        const current = aggregation.counts.get(occurrence.name);
        aggregation.counts.set(occurrence.name, {
            name: occurrence.name,
            category: occurrence.category,
            failures: (current?.failures ?? 0) + 1,
        });
    }
    aggregation.scannedObservations += batch.scannedObservations;
    aggregation.availableObservations = batch.availableObservations;
    aggregation.limits = batch.limits;
    aggregation.refreshedAt = batch.refreshedAt;
}

export function toParameterFailureInsights(
    aggregation: ParameterFailureAggregation,
    complete: boolean,
    truncationReason: ParameterFailureTruncationReason,
): ParameterFailureInsights {
    if (!aggregation.limits) throw new Error('参数失败批次尚未初始化');
    const failures = [...aggregation.counts.values()].sort(
        (left, right) => right.failures - left.failures || left.name.localeCompare(right.name),
    );
    return {
        traceName: aggregation.traceName,
        failures,
        totalParameterFailures: aggregation.occurrences.size,
        scannedObservations: aggregation.scannedObservations,
        availableObservations: aggregation.availableObservations,
        complete,
        truncationReason,
        limits: aggregation.limits,
        refreshedAt: aggregation.refreshedAt,
    };
}
